import { ref, type Ref } from 'vue';

import BookTickerChannel from '@exchange/libs/book-ticker/service/src/lib/BookTicker.channel';
import { logger } from '@exchange/libs/utils/simple-logger/src';
import type { SubscribeCallbacks } from '@exchange/libs/utils/wss/src';
import { WSIncomingEventTypes } from '@exchange/libs/utils/wss/src/lib/websocket-spot-model';

import type { BookTickMessage, BookTickData } from './book-ticker-model';

class BookTicker {
  private readonly logPrefix = 'BookTickerService:';

  private data: Dictionary<{
    bookTick: Ref<BookTickData>;
    listenerCount: number;
    unsubscribe?: () => Promise<void>;
    pendingUnsubscribe?: Promise<void>;
  }> = {};

  public async subscribe(marketId: string, callbacks: SubscribeCallbacks<{ bookTick: Ref<BookTickData> }>) {
    // If this channel/marketId is being unsubscribed, wait for it to complete before re-subscribing to avoid subscribe/unsubscribe race conditions
    await this.awaitPendingUnsubscribes(marketId);

    let marketData = this.data[marketId];

    if (!marketData) {
      marketData = {
        bookTick: ref({} as BookTickData),
        listenerCount: 1,
      };
      this.data[marketId] = marketData;

      marketData.unsubscribe = this.openChannel(marketId, {
        success: () => {
          const marketDataForId = this.data[marketId];

          if (marketDataForId) {
            callbacks.success({ bookTick: marketDataForId.bookTick });
          }
        },
        fail: callbacks.fail,
      });
    } else {
      marketData.listenerCount += 1;
      callbacks.success({ bookTick: marketData.bookTick });
    }

    return () => this.unsubscribe(marketId);
  }

  private unsubscribe(marketId: string) {
    const marketData = this.data[marketId];
    if (!marketData) return;

    marketData.listenerCount -= 1;

    if (marketData.listenerCount < 1) {
      const unsubscribePromise = marketData.unsubscribe?.().then(() => this.clear(marketId));
      if (unsubscribePromise) marketData.pendingUnsubscribe = unsubscribePromise;
    }
  }

  private async awaitPendingUnsubscribes(marketId?: string) {
    if (marketId) {
      const marketData = this.data[marketId];
      if (marketData?.pendingUnsubscribe) await marketData.pendingUnsubscribe;
    } else {
      await Promise.all(Object.values(this.data).map((marketData) => marketData?.pendingUnsubscribe));
    }
  }

  private openChannel(marketId: string, callbacks: SubscribeCallbacks) {
    // Get all markets we need to subscribe to
    const marketsToSubscribe = new Set(Object.keys(this.data));

    marketsToSubscribe.add(marketId);

    return BookTickerChannel.subscribe(
      {
        markets: Array.from(marketsToSubscribe),
        onMessage: (e: BookTickMessage) => {
          if (e.type === WSIncomingEventTypes.BOOK_TICK || e.type === WSIncomingEventTypes.BOOK_TICKER_SNAPSHOT) {
            const bookTickData: BookTickData = {
              bestBid: e.data.best_bid,
              bidAmount: e.data.bid_amount,
              bestAsk: e.data.best_ask,
              askAmount: e.data.ask_amount,
              midPrice: (Number(e.data.best_bid) + Number(e.data.best_ask)) / 2,
            };

            // logger.info(`${this.logPrefix} received book tick for ${e.instrument_code}`, bookTickData);
            this.updateBookTick(e.instrument_code, bookTickData);
          }
        },
      },
      callbacks,
    );
  }

  private updateBookTick(marketId: string, bookTickData: BookTickData) {
    const marketData = this.data[marketId];

    if (marketData) {
      marketData.bookTick.value = bookTickData;
    }
  }

  private clear(marketId: string) {
    logger.info(`${this.logPrefix} deleting ${marketId} data`);
    delete this.data[marketId];
  }
}

const bookTickerService = new BookTicker();

export default bookTickerService;
